import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { identifyVisitor, clientStatusForWire } from '@/lib/fingerprint';
import { getBlogViewFingerprint } from '@/lib/blogViewFingerprint';
import { getNavigationContext, registerPendingVisit, type TraceContext } from '@/lib/trace';

const TRACKING_INTERVAL = 2 * 60 * 1000; // 2 minutes

export const VisitorTracker = () => {
  const location = useLocation();
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const trackingEnabledRef = useRef<boolean>(true);
  // Resolves once the tracking preference has been read (first emission waits for it).
  const settingsRef = useRef<Promise<void> | null>(null);
  // Guards against a duplicated emission for the very same navigation
  // (React StrictMode remounts, mount effect + route effect, …).
  const lastEmittedRef = useRef<string | null>(null);
  // Latest navigation context, so heartbeats stay correlated with the page.
  const ctxRef = useRef<TraceContext | null>(null);

  const checkTrackingEnabled = async () => {
    try {
      const { data } = await supabase
        .from('site_settings')
        .select('setting_value')
        .eq('setting_key', 'visitor_tracking')
        .maybeSingle();

      if (data?.setting_value && typeof data.setting_value === 'object' && 'enabled' in data.setting_value) {
        trackingEnabledRef.current = data.setting_value.enabled as boolean;
      }
    } catch (error) {
      // En cas d'erreur, on garde le tracking activé par défaut
      console.warn('visitor tracking preference unavailable; keeping default');
    }
  };

  /**
   * `ctx` is always captured by the caller BEFORE any await (including the
   * tracking-preference read), so client and server logs share the same ids.
   */
  const trackVisit = async (pageUrl: string, ctx: TraceContext, referrer?: string) => {
    if (settingsRef.current) await settingsRef.current;

    // Vérifier si le tracking est activé
    if (!trackingEnabledRef.current) {
      return;
    }

    // Ne pas appeler track-visit depuis la page challenge (évite la boucle)
    if (pageUrl.startsWith('/challenge')) {
      return;
    }

    try {
      const fp = await identifyVisitor(undefined, {
        traceId: ctx.traceId,
        sessionId: ctx.sessionId,
      });

      const { data } = await supabase.functions.invoke('track-visit', {
        body: {
          page_url: pageUrl,
          referrer: referrer || document.referrer,
          session_id: ctx.sessionId,
          trace_id: ctx.traceId,
          fp_request_id: fp.requestId,
          fp_status: clientStatusForWire(fp),
          fingerprint: getBlogViewFingerprint() || null,
        },
      });

      if (
        data &&
        typeof data === 'object' &&
        (data as { challenge_required?: boolean }).challenge_required === true &&
        sessionStorage.getItem('challenge_passed') !== '1' &&
        !window.location.pathname.startsWith('/challenge')
      ) {
        const next = encodeURIComponent(window.location.pathname + window.location.search);
        window.location.replace(`/challenge?next=${next}`);
      }
    } catch (error) {
      console.warn('visit tracking failed (non blocking)');
    }
  };

  useEffect(() => {
    settingsRef.current = checkTrackingEnabled();

    // Heartbeat : garde la session active (n'émet pas la première visite,
    // celle-ci est émise une seule fois par navigation dans l'effet ci-dessous).
    intervalRef.current = setInterval(() => {
      const ctx = ctxRef.current;
      if (!ctx) return;
      const currentUrl = window.location.pathname + window.location.search;
      registerPendingVisit(trackVisit(currentUrl, ctx));
    }, TRACKING_INTERVAL);

    // Écouter les changements du paramètre en temps réel
    const channel = supabase
      .channel('visitor-tracking-settings')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'site_settings',
          filter: 'setting_key=eq.visitor_tracking'
        },
        (payload) => {
          if (payload.new && typeof payload.new === 'object' && 'setting_value' in payload.new) {
            const newValue = payload.new.setting_value as { enabled: boolean };
            trackingEnabledRef.current = newValue.enabled;
          }
        }
      )
      .subscribe((status, err) => {
        if (err) {
          console.debug('Realtime subscription error (non-critical)');
        }
      });

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      supabase.removeChannel(channel).catch(() => {
        // Suppress channel removal errors
      });
    };
  }, []);

  useEffect(() => {
    // Contexte capturé avant toute attente, et partagé avec la page Redirect.
    const navKey = location.key ?? `${location.pathname}${location.search}`;
    const ctx = getNavigationContext(navKey);
    ctxRef.current = ctx;

    // Une seule émission par navigation (montage initial inclus).
    if (lastEmittedRef.current === navKey) return;
    lastEmittedRef.current = navKey;
    registerPendingVisit(trackVisit(location.pathname + location.search, ctx));
  }, [location.key, location.pathname, location.search]);

  return null; // This component doesn't render anything
};
